// DISC.C1 (#1478) — Claude Design round-trip guided tour.
//
// A non-blocking 5-step modal that appears alongside the spec-bundle download
// the moment a user first clicks "Export to Claude Design" / "Copy Prompt". The
// download has already started; this just walks the user through what to do with
// the bundle (design in Claude Design → package the handoff ZIP → import back →
// verify the Dev Agent picked it up).
//
// Persistence is server-side: on dismiss (when "Don't show again" is checked —
// the default) it POSTs /users/me/acknowledge-feature { feature:
// 'claude_design_tour_v1' }, so the tour never re-shows on any device. The _v1
// key lets a future redesigned tour ship under a new key and show again.
//
// Dismiss is NON-BLOCKING: it closes immediately (calls onDismiss synchronously)
// and fires the acknowledgement POST in the background, so a slow / hung request
// can never trap the user in the modal (R2). `onDismiss` receives whether the
// acknowledgement was persisted (true unless the user unchecked "Don't show
// again"), so the caller can optimistically update its local state without a
// re-fetch race.

import { useState } from 'react';
import { apiFetch } from '../lib/api';

export const CLAUDE_DESIGN_TOUR_FEATURE = 'claude_design_tour_v1';

interface Props {
  open: boolean;
  /** Fires on dismiss. `persisted` = the acknowledgement was (background-)saved. */
  onDismiss: (persisted: boolean) => void;
}

interface Step {
  title: string;
  body: string;
}

// The five round-trip stages. Kept as data so the step counter, navigation, and
// the Finish-on-last-step behaviour all derive from one source of truth.
const STEPS: Step[] = [
  {
    title: 'Export',
    body:
      'Your spec bundle is downloading. It contains PROMPT.md (your project ' +
      'brief for Claude Design), tokens.json (your design system), and ' +
      'screen-context.md (your screen inventory).',
  },
  {
    title: 'Design',
    body:
      'Open claude.ai and create a new Project. Paste the full PROMPT.md ' +
      'content as the project instructions. Then prompt Claude Design to design ' +
      'your screens. Budget 20–30% of your daily token quota for a full session.',
  },
  {
    title: 'Package',
    body:
      'At the end of your Claude Design session, collect the output into a ' +
      'handoff bundle ZIP: README.md + PROMPT.md + screens/ folder. This is the ' +
      "bundle BluePrint's importer expects.",
  },
  {
    title: 'Import',
    body:
      "Return to your BluePrint project dashboard. Click 'Import Claude Design " +
      "bundle' in the sidebar. Upload the ZIP, review the mapping plan, and click " +
      'Approve & Merge.',
  },
  {
    title: 'Verify',
    body:
      "Start a new Phase 4 Development Agent session. Check for a '## Design " +
      "Reference (Claude Design)' section in the Dev Agent context — this confirms " +
      'your refined design intent is active.',
  },
];

export default function ClaudeDesignTour({ open, onDismiss }: Props) {
  // 0-indexed step pointer. The parent mounts this component ONLY while the tour
  // is open ({tourOpen && <ClaudeDesignTour .../>}), so every open is a fresh
  // mount that starts at step 1 with the box checked — no reset effect needed
  // (R2). The `open` guard below is a defensive no-op for standalone use.
  const [stepIndex, setStepIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  if (!open) return null;

  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  // Dismiss = Skip / ✕ / Finish. Closes IMMEDIATELY and persists the
  // acknowledgement in the background (fire-and-forget) only when "Don't show
  // again" is checked — so neither a slow nor a hung request can keep the modal
  // open. A failed POST just means the tour may re-show next time.
  const handleDismiss = () => {
    if (dismissing) return;
    setDismissing(true);
    const persist = dontShowAgain;
    if (persist) {
      void apiFetch('/users/me/acknowledge-feature', {
        method: 'POST',
        body: JSON.stringify({ feature: CLAUDE_DESIGN_TOUR_FEATURE }),
      }).catch(err => console.debug('[ClaudeDesignTour] acknowledge failed', err));
    }
    onDismiss(persist);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Claude Design round-trip tour"
      data-testid="claude-design-tour"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', padding: '48px 16px', overflowY: 'auto',
      }}
      // Backdrop click is a dismiss (parity with ✕), routed through the same flow.
      onClick={handleDismiss}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, background: 'var(--bg)',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)', padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span
            data-testid="claude-design-tour-step-counter"
            style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#EC4899' }}
          >
            Step {stepIndex + 1} of {STEPS.length}
          </span>
          <button
            type="button"
            data-testid="claude-design-tour-skip"
            onClick={handleDismiss}
            disabled={dismissing}
            aria-label="Skip tour"
            title="Skip"
            style={{ background: 'transparent', border: 'none', fontSize: 20, lineHeight: 1, color: 'var(--text-secondary)', cursor: dismissing ? 'not-allowed' : 'pointer' }}
          >
            ×
          </button>
        </div>

        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text)', margin: '8px 0 10px' }}>
          {step.title}
        </h2>
        <p
          data-testid="claude-design-tour-body"
          style={{ fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.55, color: 'var(--text-secondary)', margin: 0 }}
        >
          {step.body}
        </p>

        {isFirst && (
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              data-testid="claude-design-tour-dont-show-again"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
            />
            Don&rsquo;t show this again
          </label>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button
            type="button"
            data-testid="claude-design-tour-skip-text"
            onClick={handleDismiss}
            disabled={dismissing}
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 8px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: dismissing ? 'not-allowed' : 'pointer' }}
          >
            Skip
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="claude-design-tour-prev"
              onClick={() => setStepIndex(i => Math.max(0, i - 1))}
              disabled={isFirst || dismissing}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '6px 14px',
                background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                color: 'var(--text)', cursor: (isFirst || dismissing) ? 'not-allowed' : 'pointer',
                opacity: isFirst ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            {isLast ? (
              <button
                type="button"
                data-testid="claude-design-tour-finish"
                onClick={handleDismiss}
                disabled={dismissing}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '6px 14px',
                  color: 'white', background: '#EC4899', border: '1px solid #EC4899', borderRadius: 'var(--radius-sm)',
                  cursor: dismissing ? 'not-allowed' : 'pointer', opacity: dismissing ? 0.6 : 1,
                }}
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                data-testid="claude-design-tour-next"
                onClick={() => setStepIndex(i => Math.min(STEPS.length - 1, i + 1))}
                disabled={dismissing}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '6px 14px',
                  color: 'white', background: '#EC4899', border: '1px solid #EC4899', borderRadius: 'var(--radius-sm)',
                  cursor: dismissing ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
